import { __decorate } from "tslib";
import { ResourceType } from '@genesislcap/foundation-comms';
import { attr, observable, volatile } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import debounce from 'lodash.debounce';
import { dataserverCriteriaBuilder, logger, requestServerCriteriaBuilder, wasClickOutsideElement, } from '../utils';
import { Position } from './multiselect-consts';
import { multiselectStyles as styles, maxheight } from './multiselect.styles';
import { multiselectTemplate as template } from './multiselect.template';
const DEBOUNCE_TIME = 500;
export const foundationMultiselectShadowOptions = undefined;
export const defaultMultiselectConfig = {};
/**
 * @tagname %%prefix%%-multiselect
 */
export class Multiselect extends FoundationElement {
    constructor() {
        super(...arguments);
        this.name = '';
        this.all = true;
        this.allSelected = false;
        this.search = true;
        this.disabled = false;
        this.async = false;
        this.debounce = DEBOUNCE_TIME;
        this.clickOutside = (e) => this.handleClickOutside(e);
        this.open = false;
        this.options = [];
        this.selectedOptions = [];
        this.filteredAvailableOptions = [...this.options];
        this.position = Position.BELOW;
        this.initialisedOptions = false;
    }
    debounceChanged(oldValue, newValue) {
        this.debouncedInputHandler = debounce(this.asyncSetFilter, newValue);
    }
    openChanged(previous, open) {
        var _a, _b, _c, _d;
        if (open) {
            document.addEventListener('mousedown', this.clickOutside);
            (_a = this.arrowIcon) === null || _a === void 0 ? void 0 : _a.classList.add('flipped');
            (_b = this.optionsElement) === null || _b === void 0 ? void 0 : _b.classList.add('visible');
        }
        else {
            document.removeEventListener('mousedown', this.clickOutside);
            (_c = this.arrowIcon) === null || _c === void 0 ? void 0 : _c.classList.remove('flipped');
            (_d = this.optionsElement) === null || _d === void 0 ? void 0 : _d.classList.remove('visible');
        }
    }
    connectedCallback() {
        var _a;
        super.connectedCallback();
        if (this.position === Position.ABOVE) {
            (_a = this.optionsElement) === null || _a === void 0 ? void 0 : _a.classList.add('position-above');
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this.clickOutside);
    }
    optionsChanged() {
        if (this.allSelected && !this.initialisedOptions) {
            this.selectedOptions = this.options.map((option) => this.optionGetter(option));
        }
        this.filteredAvailableOptions = [...this.options];
    }
    selectedOptionsChanged() {
        if (this.$fastController.isConnected) {
            this.initialisedOptions = true;
            return;
        }
    }
    toggle() {
        if (!this.open && this.position === Position.DYNAMIC) {
            this.setDropdownPosition();
        }
        this.open = !this.open;
    }
    setDropdownPosition() {
        var _a, _b;
        const remainingSpace = (this.scrollContainer || this.parentElement).clientHeight -
            (this.offsetTop - (this.scrollContainer || this.parentElement).scrollTop);
        const totalHeight = maxheight + this.offsetHeight;
        if (remainingSpace < totalHeight) {
            (_a = this.optionsElement) === null || _a === void 0 ? void 0 : _a.classList.add('position-above');
        }
        else {
            (_b = this.optionsElement) === null || _b === void 0 ? void 0 : _b.classList.remove('position-above');
        }
    }
    optionGetter(option) {
        if (typeof option != 'string') {
            return option.value;
        }
        return option;
    }
    getItemLabel(option) {
        let value;
        if (!option) {
            return '';
        }
        if (typeof option === 'string') {
            value = option;
        }
        else {
            value = option.label || option.value || '';
        }
        return this.valueFormatter ? this.valueFormatter(value) : value;
    }
    isOptionDisabled(option) {
        if (!option || typeof option === 'string') {
            return false;
        }
        return option.disabled || false;
    }
    get getDisplayValue() {
        let displayText = '';
        switch (this.selectedOptions.length) {
            case 0: {
                displayText = this.getPlaceholder;
                break;
            }
            case 1: {
                const selectedOption = this.options.find((option) => this.optionGetter(option) === this.selectedOptions[0]);
                displayText = this.getItemLabel(selectedOption);
                break;
            }
            case this.options.length: {
                displayText = `All ${this.name}`.trim();
                break;
            }
            default: {
                displayText = `${this.selectedOptions.length} Selected`;
            }
        }
        return displayText;
    }
    get getPlaceholder() {
        return `Search ${this.name}`.trim().concat('...');
    }
    get isAllSelected() {
        return (!this.isAllOptionsDisabled && this.selectedOptions.length === this.getEnabledOptionsCount());
    }
    get isAllOptionsDisabled() {
        return this.options.length === this.options.filter(this.isOptionDisabled).length;
    }
    isOptionSelected(normalizedOption) {
        return this.selectedOptions.includes(this.optionGetter(normalizedOption));
    }
    selectCheckboxOne(normalizedOption) {
        const option = this.optionGetter(normalizedOption);
        const isIncluded = this.selectedOptions.includes(option);
        if (!isIncluded) {
            this.selectedOptions = [...this.selectedOptions, option];
        }
        else {
            this.selectedOptions = [...this.selectedOptions];
            this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1);
        }
        this.$emit('selectionChange', this.selectedOptions);
    }
    getEnabledOptionsCount() {
        return this.options.length - this.options.filter(this.isOptionDisabled).length;
    }
    selectCheckboxAll() {
        const prevAllChecked = this.selectedOptions.length === this.getEnabledOptionsCount();
        this.selectedOptions = prevAllChecked
            ? []
            : this.options
                .filter((opt) => !this.isOptionDisabled(opt))
                .map((option) => this.optionGetter(option));
        if (this.onAllSelectedCallback) {
            this.onAllSelectedCallback(!prevAllChecked);
        }
        this.$emit('selectionChange', this.selectedOptions);
    }
    inputHandler() {
        if (this.async) {
            this.loading = true;
            this.filteredAvailableOptions = [];
            this.debouncedInputHandler();
        }
        else {
            this.setFilter();
        }
    }
    setFilter() {
        this.filteredAvailableOptions = [
            ...this.options.filter((option) => {
                var _a;
                const optionValue = typeof option == 'string' ? option : (_a = option.label) !== null && _a !== void 0 ? _a : option.value;
                return optionValue.toLowerCase().startsWith(this.filterSearchValue.toLowerCase());
            }),
        ];
    }
    asyncSetFilter() {
        if (this.slottedDatasource.length > 0) {
            const resourceType = this.slottedDatasource[0].datasource.resourceType;
            if (resourceType === ResourceType.DATASERVER) {
                dataserverCriteriaBuilder(this.slottedDatasource[0], this.filterSearchValue);
            }
            if (resourceType === ResourceType.REQUEST_SERVER) {
                requestServerCriteriaBuilder(this.slottedDatasource[0], this.filterSearchValue);
            }
        }
        else {
            this.loading = false;
            logger.warn('Multiselect-datasource is not connected. Please add a datasource to use this functionality.');
        }
    }
    changeHandler(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }
    handleClickOutside(event) {
        if (wasClickOutsideElement(event, this)) {
            this.open = false;
        }
    }
}
__decorate([
    attr
], Multiselect.prototype, "name", void 0);
__decorate([
    attr({ mode: 'boolean' })
], Multiselect.prototype, "all", void 0);
__decorate([
    attr({ mode: 'boolean' })
], Multiselect.prototype, "allSelected", void 0);
__decorate([
    attr({ mode: 'boolean' })
], Multiselect.prototype, "search", void 0);
__decorate([
    attr({ mode: 'boolean' })
], Multiselect.prototype, "disabled", void 0);
__decorate([
    attr({ mode: 'boolean' })
], Multiselect.prototype, "async", void 0);
__decorate([
    attr
], Multiselect.prototype, "debounce", void 0);
__decorate([
    observable
], Multiselect.prototype, "defaultSlottedNodes", void 0);
__decorate([
    observable
], Multiselect.prototype, "slottedDatasource", void 0);
__decorate([
    observable
], Multiselect.prototype, "valueFormatter", void 0);
__decorate([
    observable
], Multiselect.prototype, "loading", void 0);
__decorate([
    observable
], Multiselect.prototype, "filterSearchValue", void 0);
__decorate([
    observable
], Multiselect.prototype, "open", void 0);
__decorate([
    observable
], Multiselect.prototype, "options", void 0);
__decorate([
    observable
], Multiselect.prototype, "selectedOptions", void 0);
__decorate([
    observable
], Multiselect.prototype, "filteredAvailableOptions", void 0);
__decorate([
    observable
], Multiselect.prototype, "scrollContainer", void 0);
__decorate([
    observable
], Multiselect.prototype, "position", void 0);
__decorate([
    observable
], Multiselect.prototype, "onAllSelectedCallback", void 0);
__decorate([
    volatile
], Multiselect.prototype, "getDisplayValue", null);
__decorate([
    volatile
], Multiselect.prototype, "getPlaceholder", null);
__decorate([
    volatile
], Multiselect.prototype, "isAllSelected", null);
__decorate([
    volatile
], Multiselect.prototype, "isAllOptionsDisabled", null);
export const foundationMultiselect = Multiselect.compose(Object.assign({ baseName: 'multiselect', template,
    styles, shadowOptions: foundationMultiselectShadowOptions }, defaultMultiselectConfig));
